"use client";

// BUG-11-CHG-01: stays on raw useAccount — modal triggers on actual connected-wallet change/disconnect, not target. Detection only, no read-path semantics.
import { useTranslations } from "next-intl";
import { useAccount } from "wagmi";
import { Button } from "@/components/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/auth";

export const WalletChangeModal = () => {
  const t = useTranslations("modals");
  const { address } = useAccount();
  const { isLoggedIn, user, isImpersonating, logout, setIsLoginDialogOpen } =
    useAuth();

  // Compare the connected wallet against the wallet the SESSION belongs to.
  //
  // This used to track a `previousAddressRef` seeded on first mount, which only
  // ever caught a switch that happened while the page was already open. A
  // mismatch that already existed at mount — token for wallet A restored from
  // localStorage while wallet B is connected — was invisible, because the ref
  // was initialised to B and "nothing changed". The app then read /user/me as A
  // while every transaction was signed by B. That is how buyers ended up
  // planted under the wrong upline: placement resolved from A's account, but
  // `msg.sender` was B.
  //
  // Deriving the mismatch instead of remembering it means there is no state to
  // seed wrong: whenever the two identities disagree, on any render including
  // the first, the modal is shown.
  //
  // Impersonation is exempt, because it is the one case where the identities
  // are SUPPOSED to disagree: the session belongs to the target user while the
  // connected wallet stays the admin's own. See
  // docs/architecture/impersonation-ux.md — reads use the effective address,
  // writes stay on the connected wallet. Without this exemption the modal is
  // permanently open and undismissable for the whole impersonation session
  // (its only button logs you out), which made the feature unusable.
  //
  // Nothing is lost by exempting it: the dangerous case this modal exists to
  // catch — signing a transaction as one wallet while reading another's account
  // — is independently blocked at checkout, where usePlacementResolution
  // resolves `wallet-mismatch` and refuses to purchase, and again server-side by
  // the read-only guard on the impersonation JWT.
  const sessionWallet = user?.wallet_address;
  const isMismatched =
    isLoggedIn &&
    !isImpersonating &&
    !!address &&
    !!sessionWallet &&
    address.toLowerCase() !== sessionWallet.toLowerCase();

  const handleChangeWallet = async () => {
    await logout();
    setIsLoginDialogOpen(true);
  };

  return (
    <Dialog open={isMismatched} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-xs space-y-4 px-8"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="w-full">
          <DialogTitle>{t("walletChange.title")}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-sm text-text-primary font-normal">
          {t("walletChange.message")}
        </DialogDescription>
        <DialogFooter className="w-full">
          <div className="w-full flex justify-center">
            <Button
              colors="secondary"
              className="w-full"
              onClick={handleChangeWallet}
            >
              {t("walletChange.reLoginButton")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
